/**
 * Contrato da camada de edição sobre a sessão viva.
 *
 * Não é uma rota (design.md, Decisão 11): a fila não é persistida, então
 * navegar para `/revisao/estudar` montaria outra sessão. Quem abre, cancela
 * e grava é a view; este módulo só nomeia o que os dois lados compartilham.
 *
 * `PUT /review/cards/:id` responde `{ updated: true }` e não devolve o cartão
 * (`updateReviewCard` em review_cards.go). O conteúdo que volta para a fila é
 * o corpo que esse PUT aceitou. Id, agendamento, prévias e a questão vinculada
 * permanecem os do cartão que já estava na memória.
 *
 * O PUT lê `occlusionMode` com os valores `hide_all` | `hide_one`. A tela fala
 * `studyMode` (`ocultar-todas` | `ocultar-uma`). A tradução fica aqui, num
 * lugar só.
 */
import type { OcclusionStudyMode, Rating, ReviewCard, ReviewHistoryEntry, ReviewMask } from '../../../sdk/@types/Revisao';
import type { UpdateCardBody } from '../../../sdk/services/ReviewService';

export const ROTULO_HISTORICO_INDISPONIVEL = 'O histórico está indisponível.';
export const ROTULO_HISTORICO_VAZIO = 'Nenhuma resposta registrada.';

/** Mesmos rótulos da barra de notas e da lista de atalhos da sessão. */
export const ROTULO_NOTA: Record<Rating, string> = {
  again: 'Errei',
  hard: 'Difícil',
  good: 'Bom',
  easy: 'Fácil',
};

/** Atalho que abre o editor. Só entra na lista quando quem estuda administra. */
export const ATALHO_EDITAR_SESSAO = { acao: 'Editar', tecla: 'E' } as const;

export type AbaEditor = 'conteudo' | 'ajustes' | 'historico';

export const ABAS_EDITOR: readonly { id: AbaEditor; rotulo: string }[] = [
  { id: 'conteudo', rotulo: 'Conteúdo' },
  { id: 'ajustes', rotulo: 'Ajustes' },
  { id: 'historico', rotulo: 'Histórico' },
];

export interface MateriaOpcao {
  id: string;
  name: string;
}

export interface RascunhoEditor {
  front: string;
  back: string;
  subjectId: string;
  tags: string[];
  frontImageKey: string;
  masks: ReviewMask[];
  studyMode: OcclusionStudyMode;
}

export type HistoricoEditor =
  | { estado: 'carregando' }
  | { estado: 'indisponivel' }
  | { estado: 'vazio' }
  | { estado: 'lista'; entradas: ReviewHistoryEntry[] };

export interface EditorNaRevisaoProps {
  card: ReviewCard;
  /** 1-based, o mesmo `posicao` que a sessão já mostra. */
  posicao: number;
  total: number;
  /**
   * Aba aberta na montagem. A sessão omite e começa em Conteúdo.
   * As histórias passam a aba que precisam mostrar. Trocar de aba
   * depois disso não lê este valor de novo e não zera o rascunho.
   */
  abaInicial?: AbaEditor;
  /**
   * `true` abaixo de 768px: folha inferior com scrim.
   * `false`: painel que a view coloca ao lado da pilha, em fluxo.
   */
  estreito: boolean;
  materias: readonly MateriaOpcao[];
  onCancelar: () => void;
  /** Cartão já fundido por `cartaoComConteudoAceito`. A view o põe na fila sem mudar o índice. */
  onSalvo: (card: ReviewCard) => void;
  salvar: (id: string, body: UpdateCardBody) => Promise<{ updated?: boolean } | void>;
  carregarHistorico: (id: string) => Promise<ReviewHistoryEntry[]>;
}

/** "cartão 12 de 40" — o ponto em que a revisão está parada. */
export function rotuloPausaFila(posicao: number, total: number): string {
  return `cartão ${posicao} de ${total}`;
}

/**
 * Tecla que abre o editor. Não é Esc nem ⌘↵ — esses pertencem à camada já
 * aberta. meta/ctrl/alt não abrem. A view ainda recusa quando o foco está
 * num campo de texto.
 */
export function teclaAbreEditor(event: {
  key: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
}): boolean {
  if (event.metaKey || event.ctrlKey || event.altKey) return false;
  return event.key === 'e' || event.key === 'E';
}

/** Esc fecha sem gravar. ⌘↵ e Ctrl+↵ gravam e fecham. Outras teclas não são desta camada. */
export function comandoDaCamada(event: {
  key: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
}): 'cancelar' | 'salvar' | null {
  if (event.key === 'Escape') return 'cancelar';
  if (event.key === 'Enter' && (event.metaKey || event.ctrlKey) && !event.altKey) return 'salvar';
  return null;
}

export function modoDaLeitura(card: ReviewCard): OcclusionStudyMode {
  const extra = card as ReviewCard & { occlusionMode?: string };
  const raw = extra.occlusionMode ?? card.studyMode;
  if (raw === 'hide_one' || raw === 'ocultar-uma') return 'ocultar-uma';
  return 'ocultar-todas';
}

export function rascunhoDoCartao(card: ReviewCard): RascunhoEditor {
  return {
    front: card.front ?? '',
    back: card.back ?? '',
    subjectId: card.subjectId ?? '',
    tags: card.tags ? card.tags.slice() : [],
    frontImageKey: card.frontImageKey ?? '',
    masks: card.masks ? card.masks.map((mask) => ({ ...mask })) : [],
    studyMode: modoDaLeitura(card),
  };
}

function mesmaLista(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function mesmasMascaras(a: readonly ReviewMask[], b: readonly ReviewMask[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const left = a[i];
    const right = b[i];
    if (
      left.x !== right.x ||
      left.y !== right.y ||
      left.w !== right.w ||
      left.h !== right.h ||
      left.label !== right.label
    ) {
      return false;
    }
  }
  return true;
}

/** Verdadeiro quando o rascunho diverge do cartão que abriu a camada. */
export function rascunhoAlterado(card: ReviewCard, rascunho: RascunhoEditor): boolean {
  const origem = rascunhoDoCartao(card);
  if (card.source === 'question') {
    return rascunho.subjectId !== origem.subjectId || !mesmaLista(rascunho.tags, origem.tags);
  }
  if (
    rascunho.front !== origem.front ||
    rascunho.back !== origem.back ||
    rascunho.subjectId !== origem.subjectId ||
    !mesmaLista(rascunho.tags, origem.tags)
  ) {
    return true;
  }
  if (card.layout !== 'occlusion') return false;
  return (
    rascunho.frontImageKey !== origem.frontImageKey ||
    rascunho.studyMode !== origem.studyMode ||
    !mesmasMascaras(rascunho.masks, origem.masks)
  );
}

/** Valor que o PUT lê. A tela continua em `ocultar-*`. */
export function occlusionModeNoFio(mode: OcclusionStudyMode): 'hide_all' | 'hide_one' {
  return mode === 'ocultar-uma' ? 'hide_one' : 'hide_all';
}

/**
 * Corpo do PUT. Questão só manda matéria e tags — o enunciado é a questão
 * vinculada. Oclusão manda `occlusionMode` no vocabulário do handler e não
 * manda `studyMode`, para um valor `ocultar-*` não ser recusado como modo.
 */
export function corpoDoSalvar(card: ReviewCard, rascunho: RascunhoEditor): UpdateCardBody {
  if (card.source === 'question') {
    return {
      subjectId: rascunho.subjectId || card.subjectId,
      tags: rascunho.tags,
    };
  }
  const body: UpdateCardBody = {
    layout: card.layout ?? 'basic',
    front: rascunho.front,
    back: rascunho.back,
    subjectId: rascunho.subjectId || card.subjectId,
    tags: rascunho.tags.slice(),
  };
  if (card.layout === 'occlusion') {
    body.frontImageKey = rascunho.frontImageKey;
    body.masks = rascunho.masks.map((mask) => ({ ...mask }));
    body.occlusionMode = occlusionModeNoFio(rascunho.studyMode);
    // Whitespace-only counts as no explanation — sent as absent, not `''`.
    body.back = rascunho.back.trim() || undefined;
  }
  return body;
}

/**
 * Cartão da fila depois de um PUT aceito. Não troca o id. Não mexe em
 * status, vencimento, intervalo, prévias, shortId nem na questão vinculada.
 */
export function cartaoComConteudoAceito(atual: ReviewCard, rascunho: RascunhoEditor): ReviewCard {
  if (atual.source === 'question') {
    return {
      ...atual,
      subjectId: rascunho.subjectId || atual.subjectId,
      tags: rascunho.tags.slice(),
    };
  }
  const proximo: ReviewCard = {
    ...atual,
    front: rascunho.front,
    back: rascunho.back,
    subjectId: rascunho.subjectId || atual.subjectId,
    tags: rascunho.tags.slice(),
  };
  if (atual.layout === 'occlusion') {
    proximo.frontImageKey = rascunho.frontImageKey;
    proximo.masks = rascunho.masks.map((mask) => ({ ...mask }));
    proximo.studyMode = rascunho.studyMode;
    // Same normalization corpoDoSalvar just sent for the PUT — keeps the
    // in-memory queue card from disagreeing with what was actually persisted.
    proximo.back = rascunho.back.trim() || undefined;
    // The due payload may still carry the previous `occlusionMode`. The next
    // open reads that field first, so the accepted mode has to replace it.
    (proximo as ReviewCard & { occlusionMode?: string }).occlusionMode =
      occlusionModeNoFio(rascunho.studyMode);
  }
  return proximo;
}

/**
 * `[]` é histórico de verdade (a rota respondeu e não há respostas).
 * Rejeição — rota ausente, 404, rede — é indisponível, nunca uma lista vazia.
 */
export async function lerHistorico(
  id: string,
  carregar: (id: string) => Promise<ReviewHistoryEntry[]>,
): Promise<HistoricoEditor> {
  try {
    const entradas = await carregar(id);
    if (!Array.isArray(entradas) || entradas.length === 0) return { estado: 'vazio' };
    return { estado: 'lista', entradas };
  } catch {
    return { estado: 'indisponivel' };
  }
}
